import React, { useEffect, useMemo, useState } from 'react';
import { COMPANY, LEAD_PRODUCTS } from './data/siteData';
import { isValidEmail, isValidName } from './formValidation';

const ContactForm = ({ onClose, initialProduct = 'betuple' }) => {
    const [email, setEmail] = useState('');
    const [name, setName] = useState('');
    const [organization, setOrganization] = useState('');
    const [phone, setPhone] = useState('');
    const [product, setProduct] = useState(initialProduct);
    const [message, setMessage] = useState('');
    const [consent, setConsent] = useState(false);
    const [status, setStatus] = useState('idle');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        setProduct(initialProduct || 'betuple');
    }, [initialProduct]);

    const emailTrim = email.trim();
    const nameTrim = name.trim();
    const organizationTrim = organization.trim();
    const phoneTrim = phone.trim();
    const messageTrim = message.trim();

    const canSubmit = useMemo(
        () =>
            isValidEmail(emailTrim)
            && isValidName(nameTrim)
            && organizationTrim.length >= 2
            && messageTrim.length >= 3
            && consent
            && !submitting,
        [emailTrim, nameTrim, organizationTrim, messageTrim, consent, submitting],
    );

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!canSubmit) return;

        setStatus('idle');
        setSubmitting(true);

        try {
            const response = await fetch('/api/v1/website/leads', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email: emailTrim.toLowerCase(),
                    first_name: nameTrim,
                    organization_name: organizationTrim,
                    phone: phoneTrim || undefined,
                    message: messageTrim,
                    locale: 'ru',
                    product: product || 'betuple',
                    lead_source: 'contact_form',
                    personal_data_consent: true,
                }),
            });

            if (!response.ok) {
                throw new Error('submit failed');
            }

            setStatus('success');
            setEmail('');
            setName('');
            setOrganization('');
            setPhone('');
            setMessage('');
            setConsent(false);
        } catch {
            setStatus('error');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section className="shield" role="dialog" aria-modal="true" aria-label="Форма обратной связи">
            <form className="form form--modal" onSubmit={handleSubmit} noValidate>
                <button type="button" className="form__close" onClick={onClose} aria-label="Закрыть">
                    ×
                </button>
                <h2 className="form__heading">Запросить пилот</h2>
                <p className="form__lead">
                    MarginLayer, DevOpsFort или bc — ответим в течение 24 часов в рабочие дни.
                </p>

                {status === 'success' ? (
                    <div className="form__alert form__alert--success" role="status">
                        <strong>Заявка отправлена</strong>
                        <p>Спасибо! Мы свяжемся с вами в ближайшее время.</p>
                        <button type="button" className="button button--primary button--form" onClick={onClose}>
                            Закрыть
                        </button>
                    </div>
                ) : (
                    <>
                        {status === 'error' ? (
                            <div className="form__alert form__alert--error" role="alert">
                                <strong>Не удалось отправить заявку</strong>
                                <p>
                                    Попробуйте ещё раз или напишите на{' '}
                                    <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>.
                                </p>
                            </div>
                        ) : null}

                        <label className="form__label" htmlFor="contact-product">Продукт *</label>
                        <select
                            id="contact-product"
                            className="form__input"
                            name="product"
                            value={product}
                            onChange={(e) => setProduct(e.target.value)}
                            required
                        >
                            {LEAD_PRODUCTS.map((item) => (
                                <option key={item.value} value={item.value}>
                                    {item.label}
                                </option>
                            ))}
                        </select>

                        <label className="form__label" htmlFor="contact-email">Почта *</label>
                        <input
                            id="contact-email"
                            className="form__input"
                            type="email"
                            name="email"
                            autoComplete="email"
                            placeholder="myname@example.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                        />

                        <label className="form__label" htmlFor="contact-name">Имя *</label>
                        <input
                            id="contact-name"
                            className="form__input"
                            type="text"
                            name="name"
                            autoComplete="name"
                            placeholder="Ваше имя"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            required
                        />

                        <label className="form__label" htmlFor="contact-organization">Организация *</label>
                        <input
                            id="contact-organization"
                            className="form__input"
                            type="text"
                            name="organization"
                            autoComplete="organization"
                            placeholder="ООО ПРИМЕР НАЗВАНИЯ ОРГАНИЗАЦИИ"
                            value={organization}
                            onChange={(e) => setOrganization(e.target.value)}
                            required
                        />

                        <label className="form__label" htmlFor="contact-phone">Телефон</label>
                        <input
                            id="contact-phone"
                            className="form__input"
                            type="tel"
                            name="phone"
                            autoComplete="tel"
                            placeholder="+7 (000) 000-00-00"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                        />

                        <label className="form__label" htmlFor="contact-message">Сообщение *</label>
                        <textarea
                            id="contact-message"
                            className="form__input form__input--text-area"
                            name="message"
                            placeholder="Интересует пилот MarginLayer / DevOpsFort / trial bc, кратко о задаче"
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            required
                        />

                        <label className="form__consent">
                            <input
                                type="checkbox"
                                checked={consent}
                                onChange={(e) => setConsent(e.target.checked)}
                            />
                            <span>
                                Согласен(на) на обработку персональных данных в соответствии с{' '}
                                <a href="/personal-data/" target="_blank" rel="noopener noreferrer">
                                    политикой обработки персональных данных
                                </a>
                                {' '}и{' '}
                                <a href="/conf-policy/" target="_blank" rel="noopener noreferrer">
                                    политикой конфиденциальности
                                </a>
                            </span>
                        </label>

                        <button
                            className="button button--primary button--form"
                            type="submit"
                            disabled={!canSubmit}
                        >
                            {submitting ? 'Отправка…' : 'Отправить'}
                        </button>
                    </>
                )}
            </form>
        </section>
    );
};

export default ContactForm;
